import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

/**
 * Custom hook that wraps any callback with flushSync to ensure synchronous DOM updates.
 * This is particularly useful for CodeMirror editors to prevent cursor jumping and
 * indentation issues in React 18.
 *
 * Note: The callback is stored in a ref to avoid unnecessary re-renders when the callback changes.
 * This means the hook will always use the latest version of the callback without recreating the wrapper.
 */
export const useFlushSyncCallback = <T extends (...args: never[]) => unknown>(callback: T): T => {
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	return useCallback(
		((...args: Parameters<T>) => {
			flushSync(() => {
				callbackRef.current(...args);
			});
		}) as T,
		[]
	);
};
